import React from 'react'
import { Box, BoxProps } from '@mui/material'
import { styled } from '@mui/material/styles'
import { Avatar } from '../Avatar'

export type CommentAvatarTone = 'gold' | 'coral' | 'violet' | 'cyan'

const avatarBg: Record<CommentAvatarTone, string> = {
  gold: '#ffcf7d',
  coral: '#fea67d',
  violet: '#be9dfd',
  cyan: '#01dafe',
}
const avatarInk: Record<CommentAvatarTone, string> = {
  gold: '#4a3510',
  coral: '#4a1e0a',
  violet: '#2d1a54',
  cyan: '#05323b',
}

export type CommentTagTone = 'pin' | 'voice'

export interface CommentTag {
  icon?: React.ReactNode
  label: React.ReactNode
  tone: CommentTagTone
  onClick?: () => void
}

export interface CommentItemProps {
  avatarLabel: string
  avatarTone?: CommentAvatarTone
  name: React.ReactNode
  /** Shows a small "HOST" mono chip next to the name */
  isHost?: boolean
  timestamp: React.ReactNode
  /** Comment body — pass pre-formatted `@mention` spans as children if needed */
  children: React.ReactNode
  tag?: CommentTag
  likeCount?: number
  liked?: boolean
  onLikeToggle?: () => void
  onReply?: () => void
}

const Row = styled(Box)({
  display: 'flex',
  gap: 10,
})

const Body = styled(Box)({
  flex: 1,
  minWidth: 0,
})

const Who = styled(Box)({
  display: 'flex',
  alignItems: 'baseline',
  gap: 7,
})

const Name = styled('span')({
  fontSize: 12.5,
  fontWeight: 660,
  color: 'var(--ds-night-ink, #fff)',
})

const HostChip = styled('span')({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 8,
  letterSpacing: '0.1em',
  color: '#ffcf7d',
  marginLeft: 6,
})

const Timestamp = styled('span')({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 8.5,
  color: 'var(--ds-night-ink-subtle, rgba(244,243,246,0.36))',
})

const Text = styled(Box)({
  fontSize: 12.5,
  lineHeight: 1.55,
  color: 'var(--ds-night-ink-muted, rgba(244,243,246,0.6))',
  marginTop: 3,
})

const tagToneColor: Record<CommentTagTone, string> = { pin: '#ffcf7d', voice: '#be9dfd' }
const tagToneBg: Record<CommentTagTone, string> = {
  pin: 'rgba(255,207,125,0.08)',
  voice: 'rgba(190,157,253,0.08)',
}
const tagToneBorder: Record<CommentTagTone, string> = {
  pin: 'rgba(255,207,125,0.22)',
  voice: 'rgba(190,157,253,0.22)',
}

const Tag = styled('button', {
  shouldForwardProp: (p) => p !== 'tone',
})<{ tone: CommentTagTone }>(({ tone }) => ({
  all: 'unset',
  boxSizing: 'border-box',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  marginTop: 6,
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 8.5,
  letterSpacing: '0.06em',
  padding: '4px 9px',
  borderRadius: 'var(--ds-radius-pill)',
  cursor: 'pointer',
  color: tagToneColor[tone],
  background: tagToneBg[tone],
  border: `1px solid ${tagToneBorder[tone]}`,
}))

const Acts = styled(Box)({
  display: 'flex',
  gap: 12,
  marginTop: 6,
})

const ActButton = styled('button')({
  all: 'unset',
  cursor: 'pointer',
  fontSize: 10.5,
  fontWeight: 600,
  color: 'var(--ds-night-ink-subtle, rgba(244,243,246,0.36))',
  transition: 'color var(--ds-duration-fast) var(--ds-ease)',
  '&:hover': {
    color: 'var(--ds-night-ink, #fff)',
  },
})

/** Single comment row — avatar, name/timestamp, text, optional pin/voice tag and like/reply actions. */
export function CommentItem({
  avatarLabel,
  avatarTone = 'gold',
  name,
  isHost,
  timestamp,
  children,
  tag,
  likeCount,
  liked,
  onLikeToggle,
  onReply,
}: CommentItemProps) {
  return (
    <Row>
      <Avatar sx={{ width: 28, height: 28, fontSize: 10, bgcolor: avatarBg[avatarTone], color: avatarInk[avatarTone] }}>
        {avatarLabel}
      </Avatar>
      <Body>
        <Who>
          <Name>
            {name}
            {isHost && <HostChip>HOST</HostChip>}
          </Name>
          <Timestamp>{timestamp}</Timestamp>
        </Who>
        <Text>{children}</Text>
        {tag && (
          <Tag type='button' tone={tag.tone} onClick={tag.onClick}>
            {tag.icon}
            {tag.label}
          </Tag>
        )}
        {(likeCount != null || onReply) && (
          <Acts>
            {likeCount != null && (
              <ActButton type='button' aria-pressed={liked} onClick={onLikeToggle}>
                ❤ {likeCount}
              </ActButton>
            )}
            {onReply && (
              <ActButton type='button' onClick={onReply}>
                Reply
              </ActButton>
            )}
          </Acts>
        )}
      </Body>
    </Row>
  )
}

export interface CommentThreadProps extends Omit<BoxProps, 'children'> {
  children: React.ReactNode
}

const ThreadRoot = styled(Box)({
  flex: 1,
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: 15,
})

/** Scrollable vertical list of `CommentItem`s — the lightbox comment rail body. */
export const CommentThread = React.forwardRef<HTMLDivElement, CommentThreadProps>(
  function CommentThread({ children, ...rest }, ref) {
    return (
      <ThreadRoot ref={ref} role='log' {...rest}>
        {children}
      </ThreadRoot>
    )
  },
)
